import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Upload, FileText, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import axios from 'axios';

interface CaprionDocumentsProps {
  value?: any;
  onChange?: (data: any) => void;
  onDone?: () => void;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  isBuilderMode?: boolean;
}

interface UploadedDocument {
  document_type: string;
  document_id?: string;
  file_name: string;
  uploaded_at: string;
  status?: string;
}

const DOCUMENT_TYPES = [
  { value: 'PAN Card', label: 'PAN Card' },
  { value: 'Aadhaar Card', label: 'Aadhaar Card' },
  { value: 'Bank Statement', label: 'Bank Statement' },
  { value: 'Income Proof', label: 'Income Proof / Salary Slip' },
  { value: 'Address Proof', label: 'Address Proof' },
  { value: 'Photo', label: 'Photograph' },
  { value: 'Cancelled Cheque', label: 'Cancelled Cheque' },
  { value: 'Identity Proof', label: 'Identity Proof' },
  { value: 'Passport', label: 'Passport' },
  { value: 'Driving License', label: 'Driving License' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Other', label: 'Other' },
];

const CaprionDocuments: React.FC<CaprionDocumentsProps> = ({
  value,
  onChange,
  onDone,
  registerSubmit,
  isBuilderMode = false,
}) => {
  const [applicationId, setApplicationId] = useState(value?.application_id || '');
  const [documentType, setDocumentType] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>(value?.documents || []);
  const [loadingList, setLoadingList] = useState(false);

  // Auto-fetch application_id on component mount
  useEffect(() => {
    const fetchApplicationId = async () => {
      // Check sessionStorage first
      const cachedAppId = sessionStorage.getItem('caprion_application_id');
      if (cachedAppId) {
        setApplicationId(cachedAppId);
        return;
      }

      // If not in cache, fetch from backend
      try {
        const response = await axios.get('/api/caprion/latest-application/');
        if (response.data.success && response.data.application_id) {
          const appId = response.data.application_id;
          setApplicationId(appId);
          // Cache for future use
          sessionStorage.setItem('caprion_application_id', appId);
          if (response.data.applicant_id) {
            sessionStorage.setItem('caprion_applicant_id', response.data.applicant_id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch application ID:', err);
      }
    };

    if (!isBuilderMode) {
      fetchApplicationId();
    }
  }, [isBuilderMode]);

  useEffect(() => {
    if (value?.application_id) {
      setApplicationId(value.application_id);
      if (value.documents) {
        setUploadedDocuments(value.documents);
      }
    }
  }, [value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPG, PNG, and PDF files are allowed');
        return;
      }
      
      setSelectedFile(file);
      setError('');
    }
  };

  const uploadDocument = async () => {
    if (!applicationId.trim()) {
      setError('Application ID is required');
      return;
    }
    if (!documentType) {
      setError('Please select document type');
      return;
    }
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('application_id', applicationId);
      formData.append('document_type', documentType);
      formData.append('document', selectedFile);

      const response = await axios.post('/api/caprion/upload-document/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const newDoc: UploadedDocument = {
          document_type: documentType,
          document_id: response.data.data.document_id,
          file_name: selectedFile.name,
          uploaded_at: new Date().toISOString(),
          status: 'uploaded',
        };
        
        const updatedDocs = [...uploadedDocuments, newDoc];
        setUploadedDocuments(updatedDocs);
        
        // Update parent component
        if (onChange) {
          onChange({
            application_id: applicationId,
            documents: updatedDocs,
          });
        }
        
        // Reset form
        setDocumentType('');
        setSelectedFile(null);
        const fileInput = document.getElementById('document-file') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setError(response.data.message || 'Failed to upload document');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload document');
      console.error('Document upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!applicationId.trim()) {
      setError('Application ID is required');
      return;
    }

    setLoadingList(true);
    setError('');

    try {
      const response = await axios.post('/api/caprion/document/list/', {
        application_id: applicationId,
      });

      if (response.data.success && response.data.data.documents) {
        setUploadedDocuments(response.data.data.documents);
        
        if (onChange) {
          onChange({
            application_id: applicationId,
            documents: response.data.data.documents,
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoadingList(false);
    }
  };

  const removeDocument = (index: number) => {
    const updatedDocs = uploadedDocuments.filter((_, i) => i !== index);
    setUploadedDocuments(updatedDocs);
    
    if (onChange) {
      onChange({
        application_id: applicationId,
        documents: updatedDocs,
      });
    }
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (isBuilderMode) return true;

    // Allow proceeding without documents - documents are optional
    // if (uploadedDocuments.length === 0) {
    //   setError('Please upload at least one document');
    //   return false;
    // }

    if (onDone) {
      onDone();
    }
    return true;
  };

  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(handleSubmit);
    }
  }, [registerSubmit, uploadedDocuments, isBuilderMode]);

  if (isBuilderMode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upload Documents</CardTitle>
          <CardDescription>
            Upload required documents for your loan application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label>Document Type</Label>
              <Select disabled>
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
              </Select>
            </div>
            <div>
              <Label>Choose File</Label>
              <Input type="file" disabled />
            </div>
            <Button disabled>
              <Upload className="mr-2 h-4 w-4" />
              Upload Document
            </Button>
            <Alert className="bg-blue-50 border-blue-200">
              <FileText className="h-4 w-4" />
              <AlertDescription>
                Preview mode - Document upload will be functional in the actual journey
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Documents</CardTitle>
          <CardDescription>
            Upload required documents for your loan application. Supported formats: JPG, PNG, PDF (Max 5MB)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="document_type">Document Type *</Label>
              <Select value={documentType} onValueChange={setDocumentType} disabled={loading}>
                <SelectTrigger id="document_type">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="document-file">Choose File *</Label>
              <Input
                id="document-file"
                type="file"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf"
                disabled={loading}
              />
              {selectedFile && (
                <p className="text-sm text-gray-500 mt-1">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={uploadDocument}
              disabled={loading || !applicationId || !documentType || !selectedFile}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Document
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {uploadedDocuments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Uploaded Documents ({uploadedDocuments.length})</CardTitle>
            <CardDescription>
              Documents uploaded for this application
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {uploadedDocuments.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">
                        {DOCUMENT_TYPES.find((t) => t.value === doc.document_type)?.label || doc.document_type}
                      </p>
                      <p className="text-sm text-gray-500">{doc.file_name}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(doc.uploaded_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-intellend-green" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDocument(index)}
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CaprionDocuments;
